import React from 'react';
import { Phone, Globe, Mail } from 'lucide-react';
import { API } from '../../pages/organization/finance/shared';
import { XIcon, InstagramIcon, FacebookIcon } from '../icons/SocialIcons';

const FONT_SIZE_PX = { small: '12px', medium: '13.5px', large: '15px' };
const MARGIN_PX = { tight: '8px', normal: '16px', wide: '28px' };
const SECURITY_LABELS = { public: 'عام', internal: 'داخلي', confidential: 'سري' };

const LOGO_SIZE_PX = { small: 36, medium: 52, large: 76 };

export default function ReportPrintFrame({ settings, identity, reportTitle, exportedByName, children }) {
  const s = settings || {};
  const identityCfg = s.identity || {};
  const header = s.header || {};
  const footer = s.footer || {};
  const page = s.page || {};
  const security = s.security || {};
  const logoUrl = identity?.logo_file_id ? `${API}/files/${identity.logo_file_id}` : null;
  const now = new Date();
  const logoSize = LOGO_SIZE_PX[header.logo_size] || LOGO_SIZE_PX.medium;
  const showLogo = identityCfg.show_logo !== false && header.show_logo !== false && logoUrl;
  const logoPosition = header.logo_position || 'right';

  const nameBlock = (align) => (
    <div style={{ textAlign: align }}>
      {identityCfg.show_name !== false && header.show_name !== false && (
        <h1 style={{ fontSize: '18px', fontWeight: 800, color: page.header_color || '#16233b', margin: 0 }}>{identity?.club_name || 'المنشأة'}</h1>
      )}
      {header.show_title !== false && reportTitle && <p style={{ fontSize: '13px', fontWeight: 700, color: '#334155', margin: '4px 0 0' }}>{reportTitle}</p>}
      {header.show_generated_date !== false && <p style={{ fontSize: '10px', color: '#64748b', margin: '4px 0 0' }}>تاريخ الإنشاء: {now.toLocaleDateString('ar-SA')} {now.toLocaleTimeString('ar-SA')}</p>}
      {header.show_exported_by !== false && exportedByName && <p style={{ fontSize: '10px', color: '#64748b', margin: '2px 0 0' }}>صدّره: {exportedByName}</p>}
    </div>
  );
  const logoImg = showLogo && <img src={logoUrl} alt="شعار المنشأة" style={{ height: logoSize, width: logoSize, objectFit: 'contain', background: '#fff', borderRadius: '8px', flexShrink: 0 }} />;

  const showPhoneLine = footer.show_contact !== false && identityCfg.show_contact !== false && Boolean(identity?.contact_info?.phone);
  const showEmailLine = footer.show_contact !== false && identityCfg.show_contact !== false && Boolean(identity?.contact_info?.email);
  const showWebsiteLine = identityCfg.show_website !== false && Boolean(identity?.website_url);
  const socialLinks = [
    identity?.instagram_url && { url: identity.instagram_url, label: 'انستقرام', Icon: InstagramIcon },
    identity?.twitter_url && { url: identity.twitter_url, label: 'تويتر', Icon: XIcon },
    identity?.facebook_url && { url: identity.facebook_url, label: 'فيسبوك', Icon: FacebookIcon },
  ].filter(Boolean);
  const showSocialRow = identityCfg.show_social !== false && socialLinks.length > 0;
  const showContactRow = showPhoneLine || showEmailLine || showWebsiteLine;

  return (
    <div
      dir="rtl"
      style={{ fontFamily: page.font_family || 'Cairo, sans-serif', fontSize: FONT_SIZE_PX[page.font_size] || FONT_SIZE_PX.medium, position: 'relative', padding: MARGIN_PX[page.margin] || MARGIN_PX.normal }}
      data-testid="report-print-frame"
    >
      {security.watermark_enabled && security.watermark_text && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }} data-testid="report-watermark">
          <span style={{ fontSize: '54px', fontWeight: 800, color: 'rgba(22,35,59,0.08)', transform: 'rotate(-30deg)', whiteSpace: 'nowrap' }}>{security.watermark_text}</span>
        </div>
      )}

      <div style={{ position: 'relative', zIndex: 1 }}>
        {security.level && security.level !== 'public' && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '4px' }}>
            <span data-testid="report-security-badge" style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px', background: security.level === 'confidential' ? '#fee2e2' : '#fef3c7', color: security.level === 'confidential' ? '#b91c1c' : '#92400e' }}>
              {SECURITY_LABELS[security.level]}
            </span>
          </div>
        )}

        {!header.hide && (
          logoPosition === 'center-stack' ? (
            <div data-testid="report-header" style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
              borderBottom: `2px solid ${page.header_color || '#16233b'}`, paddingBottom: '10px', marginBottom: '14px',
            }}>
              {logoImg}
              {nameBlock('center')}
            </div>
          ) : logoPosition === 'center' ? (
            <div data-testid="report-header" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
              borderBottom: `2px solid ${page.header_color || '#16233b'}`, paddingBottom: '10px', marginBottom: '14px',
            }}>
              <div style={{ flex: '1 1 0', textAlign: 'right' }}>
                {identityCfg.show_name !== false && header.show_name !== false && (
                  <h1 style={{ fontSize: '18px', fontWeight: 800, color: page.header_color || '#16233b', margin: 0 }}>{identity?.club_name || 'المنشأة'}</h1>
                )}
                {header.show_title !== false && reportTitle && <p style={{ fontSize: '13px', fontWeight: 700, color: '#334155', margin: '4px 0 0' }}>{reportTitle}</p>}
              </div>
              <div style={{ flex: '0 0 auto' }}>{logoImg}</div>
              <div style={{ flex: '1 1 0', textAlign: 'left' }}>
                {header.show_generated_date !== false && <p style={{ fontSize: '10px', color: '#64748b', margin: 0 }}>{now.toLocaleDateString('ar-SA')} {now.toLocaleTimeString('ar-SA')}</p>}
                {header.show_exported_by !== false && exportedByName && <p style={{ fontSize: '10px', color: '#64748b', margin: '2px 0 0' }}>صدّره: {exportedByName}</p>}
              </div>
            </div>
          ) : (
            <div data-testid="report-header" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexDirection: logoPosition === 'left' ? 'row-reverse' : 'row', gap: '12px',
              borderBottom: `2px solid ${page.header_color || '#16233b'}`, paddingBottom: '10px', marginBottom: '14px',
            }}>
              {logoImg}
              {nameBlock('right')}
            </div>
          )
        )}

        {children}

        {!footer.hide && (
          <div data-testid="report-footer" data-show-page-number={header.show_page_number !== false ? '1' : '0'} style={{
            borderTop: footer.divider !== false ? '1px solid #e2e8f0' : 'none', marginTop: '16px', paddingTop: '8px', fontSize: '10px', color: '#64748b',
          }}>
            {footer.show_generated_by !== false && (
              <p style={{ textAlign: 'center', margin: '0 0 6px', fontWeight: 600, color: '#475569' }}>
                تم إنشاء هذا التقرير تلقائياً بواسطة نظام إدارة {identity?.club_name || 'المنشأة'}
              </p>
            )}
            {showContactRow && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '18px', flexWrap: 'wrap', marginBottom: '6px' }}>
                {showPhoneLine && <span dir="ltr" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Phone size={11} />{identity.contact_info.phone}</span>}
                {showEmailLine && <span dir="ltr" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Mail size={11} />{identity.contact_info.email}</span>}
                {showWebsiteLine && <span dir="ltr" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Globe size={11} />{identity.website_url}</span>}
              </div>
            )}
            <div style={{
              display: 'flex', flexWrap: 'wrap', flexDirection: footer.alignment === 'center' ? 'column' : 'row',
              alignItems: footer.alignment === 'center' ? 'center' : 'stretch', textAlign: footer.alignment === 'center' ? 'center' : 'inherit',
              justifyContent: footer.alignment === 'center' ? 'center' : 'space-between', gap: '6px',
            }}>
              <span>{footer.custom_text || `© ${now.getFullYear()} ${identity?.club_name || 'المنشأة'} — جميع الحقوق محفوظة`}</span>
              {security.show_export_datetime !== false && <span>{now.toLocaleDateString('ar-SA')}</span>}
            </div>
            {showSocialRow && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '22px', marginTop: '8px', flexWrap: 'wrap' }}>
                {socialLinks.map(({ label, Icon }) => (
                  <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#0d9488', fontWeight: 600 }}>
                    <Icon className="h-3 w-3" />{label}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
